// activity.js — libellé d'activité d'un joueur (posé dans le statut Nakama, lu par ses amis).

// TYPE D'APPAREIL d'un ami depuis son statut Nakama (`d`:'m'|'p', injecté par nakamaAuth.updateStatus).
// Renvoie '📱' (mobile/tactile), '💻' (PC), ou '' si inconnu. Utilisé dans la liste des amis en ligne.
export function deviceFromStatus(status) {
  try {
    const s = typeof status === 'string' ? (status ? JSON.parse(status) : {}) : (status || {});
    if (s.d === 'm') return '📱';
    if (s.d === 'p') return '💻';
  } catch (e) { /* */ }
  return '';
}

// Déduit l'activité courante depuis l'URL de l'AppStore.
export function activityForPath(pathname) {
  const p = String(pathname || '');
  if (/^\/(store|jeux)/.test(p)) return 'Parcourt les jeux';
  if (/^\/videos/.test(p)) return 'Regarde des vidéos';
  if (/^\/communaute/.test(p)) return 'Dans la communauté';
  // /salons = la PAGE « En ligne » (pas forcément dans un salon). Le vrai statut « Dans un salon <jeu> »
  // n'est publié QUE quand on est effectivement dans un salon (LobbyPanel, quand inRoom).
  if (/^\/salons/.test(p)) return 'Dans la section En ligne';
  if (/^\/classements/.test(p)) return 'Consulte les classements';
  // Chaîne précise si l'URL la nomme (/channels/horizon → « Regarde la chaîne Horizon »).
  const ch = p.match(/^\/(channels|chaines)\/([^/?#]+)/);
  if (ch) { const n = decodeURIComponent(ch[2]).replace(/[-_]+/g, ' '); return `Regarde la chaîne ${n.charAt(0).toUpperCase()}${n.slice(1)}`; }
  if (/^\/(channels|chaines)/.test(p)) return 'Regarde les chaînes';
  if (/^\/premium/.test(p)) return 'Découvre Premium';
  if (/^\/compte/.test(p)) return 'Dans son espace membre';
  if (p === '/' || /^\/(accueil)?$/.test(p)) return 'Navigue dans les menus';
  return 'Navigue dans les menus';
}

// Statut détaillé par ONGLET de l'espace membre (le path seul ne voit pas le ?tab). Renvoie null si l'onglet
// n'a pas de statut dédié → on garde « Dans son espace membre ».
export function activityForAccountTab(tab) {
  const M = {
    teams: 'Personnalise son Master', notifs: 'Consulte ses notifications', messages: 'Écrit un message',
    amis: 'Gère ses amis', groupes: 'Gère ses groupes', bibliotheque: 'Parcourt sa bibliothèque',
    replays: 'Regarde ses replays', premium: 'Découvre Premium', niveau: 'Consulte sa progression',
  };
  return M[tab] || null;
}

// Libellés de MODE de jeu (harmonisés entre jeux) pour le statut détaillé.
const MODE_LABELS = {
  coop_ia: 'Coop vs IA', coop_online: 'Coop vs Online', pvp: '2 joueurs', ai: 'vs IA',
  classique: 'Classique', blitz: 'Blitz', arsenal: 'Arsenal', deluge: 'Déluge', jetpack: 'Jetpack', powers: 'Super-Pouvoirs',
  oneshot: 'One-shot', multi: 'Multi-vies',
};
export function modeLabel(mode) { return mode ? (MODE_LABELS[mode] || String(mode)) : ''; }

// Libellé de statut « en jeu » : jeu précis + mode (ex. « 🎮 Joue à VertiVers Battle · Coop vs IA »).
export function playingLabel(gameTitle, mode) {
  const m = modeLabel(mode);
  return `🎮 Joue à ${gameTitle}${m ? ` · ${m}` : ''}`;
}

// Décide CE QU'ON PUBLIE réellement dans le statut, selon la confidentialité. `detailed` = { a, vg? }
// (l'activité complète voulue). Retourne :
//   null            → statut MASQUÉ (l'appelant fait updateStatus(''))
//   { a } / { a,vg } → à publier.
// Visibilité : 'hidden' (rien), 'generic' (juste « En ligne »/« Absent »), 'detailed' (activité précise).
// showGame=false enlève le `vg` (état de partie) même en mode détaillé.
export function statusPayload(privacy, detailed) {
  const p = privacy || {};
  const vis = p.statusVisibility || (p.showOnline === false ? 'hidden' : 'detailed');
  if (p.showOnline === false || vis === 'hidden') return null;
  if (vis === 'generic') return { a: (p.status === 'away') ? 'Absent 💤' : 'En ligne' };
  const d = detailed || { a: 'En ligne' };
  if (p.showGame === false && d.vg) return { a: d.a };   // détaillé mais jeu masqué → pas d'état de partie
  return d;
}

// Extrait le libellé d'activité d'un statut Nakama (chaîne JSON { a } posée par updateStatus).
export function labelFromStatus(status) {
  try {
    const s = typeof status === 'string' ? JSON.parse(status) : (status || {});
    return s.a || s.activity || null;
  } catch { return null; }
}

// Extrait l'état de PARTIE structuré (vg) d'un statut — { kind:'salon', game, slug, mode, priv, count, max,
// mid, phase:'lobby'|'ingame', ready } — publié par le salon (LobbyPanel). Permet à un ami de VOIR la partie
// en direct (mode, public/privé, joueurs, phase) et de la REJOINDRE. null si absent (confidentialité/pas en jeu).
export function vgFromStatus(status) {
  try {
    const s = typeof status === 'string' ? JSON.parse(status) : (status || {});
    return (s && s.vg && typeof s.vg === 'object') ? s.vg : null;
  } catch { return null; }
}
